import {
    Button,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTrigger,
} from '@repo/shadcn';
import { Edit } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { IwForm } from '@components/form';
import { TextField } from '@components/form/fields';

import { useUpdateBalanceHistoryMutation } from '@/features/slice/balance-history/balance-history-api-slice';
import { type BalanceHistoryItem } from '@/features/slice/balance-history/balance-history.schema';
import { toastError } from '@/utils';

const EditBalanceFormSchema = z.object({
    starting_balance: z
        .preprocess(
            (value) => (value === '' ? undefined : value),
            z.coerce.number().min(0, 'Starting balance must be non-negative'),
        )
        .refine(
            (value) => Number.isFinite(value),
            'Starting balance is required',
        ),
    ending_balance: z.preprocess(
        (value) => (value === '' ? undefined : value),
        z.coerce
            .number()
            .min(0, 'Ending balance must be non-negative')
            .optional(),
    ),
});

type EditBalanceFormValues = z.infer<typeof EditBalanceFormSchema>;

type EditBalanceDialogProps = {
    row: BalanceHistoryItem;
};

export const EditBalanceDialog = ({ row }: EditBalanceDialogProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [updateBalanceHistory, { isLoading }] =
        useUpdateBalanceHistoryMutation();

    const initialValues: EditBalanceFormValues = {
        starting_balance: Number(row.starting_balance || 0),
        ending_balance:
            row.ending_balance != null ? Number(row.ending_balance) : undefined,
    };

    const handleSubmit = async (values: EditBalanceFormValues) => {
        try {
            await updateBalanceHistory({
                id: row.id,
                starting_balance: values.starting_balance,
                ending_balance: values.ending_balance,
            }).unwrap();

            toast.success('Balance updated');
            setIsOpen(false);
        } catch (error) {
            toastError(error, 'EditBalanceDialog: Failed to update balance.');
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                {/*<Button type={'button'} variant={'outline'} size={'sm'}>
                    Edit
                </Button>*/}

                <button
                    className={
                        'absolute right-6 top-4 size-2 group-hover:opacity-100'
                    }
                >
                    <Edit className="size-4" />
                </button>
            </DialogTrigger>

            <DialogContent>
                <DialogHeader>
                    <h3 className={'text-lg font-bold'}>Edit Balance</h3>
                </DialogHeader>

                <IwForm
                    schema={EditBalanceFormSchema}
                    initialValues={initialValues}
                    onSubmit={handleSubmit as any}
                    className={'mt-4 space-y-4'}
                >
                    <TextField<EditBalanceFormValues>
                        name={'starting_balance'}
                        label={'Starting Balance'}
                        type={'number'}
                        required={true}
                        inputProps={{ step: '0.01', min: '0' }}
                    />

                    {/*<TextField<EditBalanceFormValues>
                        name={'ending_balance'}
                        label={'Ending Balance'}
                        type={'number'}
                        required={false}
                        inputProps={{ step: '0.01', min: '0' }}
                    />*/}

                    <div className={'flex justify-end gap-2'}>
                        <Button
                            type={'button'}
                            variant={'outline'}
                            onClick={() => setIsOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button type={'submit'} disabled={isLoading}>
                            {isLoading ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </IwForm>
            </DialogContent>
        </Dialog>
    );
};

EditBalanceDialog.displayName = 'EditBalanceDialog';
